import { ApolloError, useLazyQuery } from '@apollo/client'
import { useCallback, useRef } from 'react'
import { useDispatch } from 'react-redux'
import {
  UserProvidersGetQuery,
  UserProvidersGetQueryVariables,
} from 'src/generated'
import providersSlice from '../../../../redux/providers/slice'
import USER_PROVIDERS_QUERY from './userProvidersQuery.gql'

type ReturnType = [
  () => Promise<UserProvidersGetQuery['userProvidersGet']['providers'] | null>,
  {
    data?: UserProvidersGetQuery
    loading: boolean
    error?: ApolloError
  },
]

const TTL_MS = 2.5 * 60 * 1000 // 2.5 minutes

export const useUserProvidersGetLazy = (): ReturnType => {
  const [query, { data, loading, error }] = useLazyQuery<
    UserProvidersGetQuery,
    UserProvidersGetQueryVariables
  >(USER_PROVIDERS_QUERY, {
    // Which providers the operator belongs to: it decides whether they are
    // sent to onboarding. Cached below with a TTL of its own, for the same
    // reason as `useMeLazyQuery`.
    fetchPolicy: 'no-cache',
  })

  const dispatch = useDispatch()
  const cachedProvidersRef = useRef<
    UserProvidersGetQuery['userProvidersGet']['providers'] | null
  >(null)
  const cacheTimestampRef = useRef<number | null>(null)

  const userProvidersGet = useCallback(async () => {
    const now = Date.now()

    // Use cached value if it's still valid
    if (
      cachedProvidersRef.current &&
      cacheTimestampRef.current &&
      now - cacheTimestampRef.current < TTL_MS
    ) {
      return cachedProvidersRef.current
    }

    const { data } = await query()

    const providers = data?.userProvidersGet.providers ?? []

    // Store in ref cache
    cachedProvidersRef.current = providers
    cacheTimestampRef.current = now

    dispatch(
      providersSlice.actions.setProviders({
        providers,
      }),
    )

    return providers
  }, [query, dispatch])

  return [
    userProvidersGet,
    {
      data,
      loading,
      error,
    },
  ]
}
